/* eslint-disable @typescript-eslint/no-unsafe-member-access -- TODO: fix this */

/* eslint-disable @typescript-eslint/no-unsafe-assignment -- TODO: fix this */

/* eslint-disable @typescript-eslint/no-explicit-any -- TODO: fix this */
import type { MouseEventHandler } from "react";
import { NodeViewWrapper } from "@tiptap/react";
import { truncate } from "lodash";
import { useQuery } from "react-query";
import { t } from "@/i18n-js/instance";
import { Icon } from "@circle-react/components/shared/Icon";
import { FileTypeIcon } from "@circle-react/components/shared/uikit/TipTap/AttachmentPreviews/FileTypeIcon";
import { useBlockEditorContext } from "@circle-react/components/shared/uikit/TipTapBlockEditor";
import { useFilePreviewModal } from "@circle-react/components/shared/uikit/TipTapBlockEditor/FileExtension/FilePreviewModal";
import { Typography } from "@circle-react/components/shared/uikit/Typography";
import { formatFileSize } from "@circle-react/helpers/fileSizeHelpers";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { internalApi } from "@circle-react/helpers/urlHelpers";
import { contentTypeParser } from "@circle-react-shared/uikit/TipTap/utilities/contentTypeParser";
import { AudioPlayer } from "@circle-react-shared/uikit/TipTapBlockEditor/FileExtension/AudioPlayer";
import { VideoRenderer } from "@circle-react-shared/uikit/TipTapBlockEditor/FileExtension/VideoRenderer";
import { CloseButton } from "./CloseButton";
import { LinkPreview } from "./LinkPreview";
import { VoiceMessageRenderer } from "./VoiceMessageRenderer";

export interface RendererProps {
  deleteNode: () => void;
  editor: any;
  node: any;
}

export const Renderer = ({ node, editor, deleteNode }: RendererProps) => {
  const isEditable = editor.isEditable;
  const filePreviewModal = useFilePreviewModal();
  const {
    localInlineAttachments,
    inlineAttachments,
    addToLocalInlineAttachments,
    shouldPreviewFilesAsLinks,
  } = useBlockEditorContext() ?? {};
  const signedId = node.attrs?.signed_id;
  const attachment =
    localInlineAttachments?.find(
      attachment => attachment.signed_id === signedId,
    ) ||
    inlineAttachments?.find(attachment => attachment.signed_id === signedId);

  const { isLoading } = useQuery(
    internalApi.richTextFields.fetchAttachment({ signedId }),
    {
      enabled: !attachment && isEditable,
      retry: false,
      retryOnMount: false,
      onSuccess: addToLocalInlineAttachments,
    },
  );

  const handleDelete: MouseEventHandler<HTMLButtonElement> = event => {
    deleteNode();
    event.preventDefault();
    event.stopPropagation();
  };

  if (isLoading) {
    return (
      <NodeViewWrapper as="div" className="my-4">
        <Icon type="loader" size={16} />
      </NodeViewWrapper>
    );
  }

  if (!attachment) {
    return (
      <NodeViewWrapper
        as="div"
        className="bg-tertiary group flex items-start gap-2 rounded-md p-4"
      >
        {isEditable && <CloseButton onClick={handleDelete} />}
        <Icon type="16-trash-bin-2" size={20} />

        <Typography.BodySm as="span">
          {t("tiptap.file_not_found")}
        </Typography.BodySm>
      </NodeViewWrapper>
    );
  }

  if (shouldPreviewFilesAsLinks) {
    return (
      <LinkPreview
        attachment={attachment}
        isEditable={isEditable}
        onDelete={handleDelete}
      />
    );
  }

  const isNativelySupportedVideo = contentTypeParser.isNativelySupportedVideo(
    attachment.content_type,
  );

  const isVideoTranscodingEnabled =
    contentTypeParser.isVideo(attachment.content_type) &&
    Boolean(attachment.media_transcoding);

  // Renders the video player only for videos that are natively supported in
  // most modern browsers or videos that are or will be transcoded.
  const isVideo = isNativelySupportedVideo || isVideoTranscodingEnabled;
  const isAudio = contentTypeParser.isAudio(attachment.content_type);
  const isPDF = contentTypeParser.isPDF(attachment.content_type);
  const isRegularFile = !isVideo && !isAudio && !isPDF;
  const isVoiceMessage = Boolean(attachment.metadata?.voice_message);

  const wrapperAnchorProps = isAudio
    ? {}
    : {
        href: attachment.url,
        download: true,
      };

  if (isVideo) {
    return (
      <VideoRenderer
        attachment={attachment}
        isTranscodingEnabled={isVideoTranscodingEnabled}
      />
    );
  }

  if (isVoiceMessage) {
    return (
      <VoiceMessageRenderer
        attachment={attachment}
        isEditable={isEditable}
        onDelete={handleDelete}
      />
    );
  }

  return (
    <NodeViewWrapper
      as="div"
      id="file-wrapper"
      className={classNames(
        "bg-secondary hover:bg-tertiary border-primary group relative w-full flex-col rounded border px-3 py-2",
        {
          "max-w-[350px]": isRegularFile,
          "max-w-[500px]": isAudio || isPDF,
        },
      )}
    >
      {isEditable && <CloseButton onClick={handleDelete} />}
      <a
        {...wrapperAnchorProps}
        className="inline-flex items-center space-x-3 truncate"
      >
        <FileTypeIcon content_type={attachment.content_type} />
        <div className="flex flex-col truncate">
          <Typography.LabelSm weight="semibold">
            {truncate(attachment.filename, { length: 35 })}
          </Typography.LabelSm>
          <Typography.LabelXs weight="regular">
            {formatFileSize(attachment.byte_size)}
          </Typography.LabelXs>
        </div>
      </a>
      {isPDF && (
        <div className="relative mt-2 flex max-h-[500px] items-center justify-center overflow-hidden">
          <button
            type="button"
            onClick={() => {
              void filePreviewModal.show({ file: attachment });
            }}
            className="absolute left-0 top-0 size-full cursor-zoom-in"
            aria-label={t("view_attachment")}
          />
          {attachment.image_variants?.thumbnail && (
            <img
              loading="lazy"
              className="h-48 w-full object-cover object-top"
              src={attachment.image_variants.thumbnail}
              alt={t("tiptap.pdf_preview")}
            />
          )}
        </div>
      )}
      {isAudio && (
        <AudioPlayer
          src={attachment.audio_variants?.optimized?.url || attachment.url}
          downloadUrl={attachment.url}
        />
      )}
    </NodeViewWrapper>
  );
};
